import { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  getLeaveBalanceFromUserAndMeta,
  getAllEmployees,
  getGoalById,
  getGoals,
  deleteGoal,
} from "../utils";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import { CircularProgress } from "@mui/material";
import { Button } from "../components";

import usePersistData from "../store/usePersistData.js";
import { getAuth } from "firebase/auth";

const LeaveDetail = () => {
  const location = useLocation();
  const { employeeId: paramEmployeeId } = useParams();
  const { goalIdk } = location.state || {};

  const navigate = useNavigate();

  const [employeeId, setEmployeeId] = useState(paramEmployeeId || "");
  const [goalId, setGoalId] = useState(goalIdk || "");
  const [leavesData, setLeavesData] = useState(null);
  const [leaveBalance, setLeaveBalance] = useState(null);
  const [loading, setLoading] = useState(null);
  const [employeeDetails, setEmployeeDetails] = useState(null);
  const [goal, setGoal] = useState(null);
  const { user } = usePersistData();
  const [goals, setGoals] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedGoal, setSelectedGoal] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [id, setId] = useState(0);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    const userUid = user?.uid || getAuth().currentUser?.uid;
    setId(userUid);

    const fetchGoals = async () => {
      setLoading(true);
      try {
        let userGoals = await getGoals(user);
        console.log("Initial Goals:", userGoals);

        if (!Array.isArray(userGoals)) {
          console.error("Error: userGoals is not an array", userGoals);
          return;
        }

        console.log("Filter:", filter);
        console.log("Employee ID:", employeeId);

        if (filter === "ownCreated") {
          userGoals = userGoals.filter((goal) => goal.managerId === id);
          console.log("Filtered (Own Created) Goals:", userGoals);
        } else if (filter === "managerCreated") {
          userGoals = userGoals.filter((goal) => goal.managerId !== id);
          console.log("Filtered (Manager Created) Goals:", userGoals);
        }

        // Apply employeeId filter
        userGoals = userGoals.filter((goal) => goal.empId === employeeId);
        console.log("Filtered (Employee) Goals:", userGoals);

        // Sorting by date
        const sortedGoals = userGoals.sort(
          (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
        );
        console.log("Sorted Goals:", sortedGoals);

        setGoals(sortedGoals);
      } catch (error) {
        console.error("Error fetching goals:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchGoals();
  }, [user, refreshKey, filter]);

  useEffect(() => {
    if (employeeId) {
      fetchLeaveBalance(employeeId);
      fetchEmployeeDetails(employeeId);
    }
  }, [employeeId]);

  useEffect(() => {
    if (goalId) {
      fetchGoal(goalId);
    }
  }, [goalId]);

  const fetchGoal = async (goalId) => {
    setLoading(true);
    try {
      const goalDetails = await getGoalById(goalId);
      const matchingGoals = goalDetails.find(
        (employee) => employee.empId === employeeId
      );

      setGoal(matchingGoals);
    } catch (error) {
      console.error("Error fetching goal:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployeeDetails = async (employeeId) => {
    setLoading(true);
    try {
      const allEmployees = await getAllEmployees(user.email);

      const matchingEmployee = allEmployees.find(
        (employee) => employee.id === employeeId
      );
      if (!matchingEmployee) {
        throw new Error("Employee not found.");
      }
      if (matchingEmployee) {
        setEmployeeDetails(matchingEmployee);
      } else {
        setEmployeeDetails(null);
      }
    } catch (error) {
      console.error("Error fetching employee details:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchLeaveBalance = async (employeeId) => {
    try {
      const { leavesData, leaveBalance } = await getLeaveBalanceFromUserAndMeta(
        employeeId
      );
      setLeavesData(leavesData);
      setLeaveBalance(leaveBalance);
    } catch (error) {
      console.error("Error fetching leave balance:", error);
    }
  };

  const calculateAvailedLeaves = (total, remaining) => total - remaining;

  const handleClickOpen = (goal) => {
    setSelectedGoal(goal);
    setOpenDialog(true);
  };

  const handleClose = () => {
    setOpenDialog(false);
    setSelectedGoal(null);
  };

  const handleDeleteGoal = async () => {
    try {
      if (selectedGoal?.id) {
        await deleteGoal(selectedGoal.id);
        setRefreshKey((prevKey) => prevKey + 1);
      }
      handleClose();
    } catch (error) {
      console.error("Error deleting goal:", error);
    }
  };

  return (
    <div className="py-16 px-5">
      {loading ? (
        <div className="flex justify-center items-center">
          <CircularProgress />
        </div>
      ) : (
        <>
          <div>
            <p className="font-bold text-xl py-4 text-color-8">
              Employee Details:
            </p>
            <div className="py-2">
              <p className="text-base font-semibold text-color-7">
                Name: {employeeDetails?.firstName || ""}
              </p>
              <p className="text-base font-semibold text-color-7">
                Email: {employeeDetails?.email || ""}
              </p>
              <p className="text-base font-semibold text-color-7">
                Phone Number: {employeeDetails?.phoneNo || ""}
              </p>
            </div>
          </div>

          <p className="font-bold text-xl py-4 text-color-8">Leaves Details:</p>
          {leavesData && leaveBalance && (
            <TableContainer
              component={Paper}
              sx={{ border: "2px solid border", borderRadius: "16px" }}
            >
              <Table sx={{ minWidth: 650 }} aria-label="leave balance table">
                <TableHead sx={{ backgroundColor: "#5D6B7F" }}>
                  <TableRow>
                    <TableCell sx={{ color: "#FFF" }} align="center">
                      Category
                    </TableCell>
                    <TableCell sx={{ color: "#FFF" }} align="center">
                      Carry Forward
                    </TableCell>
                    <TableCell sx={{ color: "#FFF" }} align="center">
                      Total
                    </TableCell>
                    <TableCell sx={{ color: "#FFF" }} align="center">
                      Availed
                    </TableCell>
                    <TableCell sx={{ color: "#FFF" }} align="center">
                      Remaining
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {["al", "sl", "cl", "totalLeaves"].map((leaveType, index) => (
                    <TableRow key={index}>
                      <TableCell align="center">
                        {leaveType === "al"
                          ? "Annual Leave"
                          : leaveType === "sl"
                          ? "Sick Leave"
                          : leaveType === "cl"
                          ? "Casual Leave"
                          : "Total Leaves"}
                      </TableCell>
                      <TableCell align="center">0</TableCell>
                      <TableCell align="center">
                        {leavesData[leaveType]}
                      </TableCell>
                      <TableCell align="center">
                        {calculateAvailedLeaves(
                          leavesData[leaveType],
                          leaveBalance[leaveType]
                        ) || "0"}
                      </TableCell>
                      <TableCell align="center">
                        {leaveBalance[leaveType]}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}

      <p className="font-bold text-xl py-4 text-color-8 mt-5"> Goals:</p>
      <TableContainer
        component={Paper}
        sx={{ border: "2px solid border", borderRadius: "16px" }}
      >
        <Table sx={{ minWidth: 650 }} aria-label="leave balance table">
          <TableHead sx={{ backgroundColor: "#5D6B7F" }}>
            <TableRow>
              <TableCell sx={{ color: "#FFF" }} align="center">
                Goal Title
              </TableCell>
              <TableCell sx={{ color: "#FFF" }} align="center">
                Employee
              </TableCell>
              <TableCell sx={{ color: "#FFF" }} align="center">
                Progress
              </TableCell>
              <TableCell sx={{ color: "#FFF" }} align="center">
                Goal Type
              </TableCell>
              <TableCell sx={{ color: "#FFF" }} align="center">
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : goals.length > 0 ? (
              goals.map((goal, index) => (
                <TableRow
                  key={index}
                  hover={user.designation === "EMPLOYEE"}
                  onClick={(e) => {
                    {
                      goal?.managerId === id
                        ? navigate(`/goalsDetails/${goal?.managerId}`, {
                            state: { goalIdk: goal?.id },
                          })
                        : navigate(`/goalsDetails/${goal?.empId}`, {
                            state: { goalIdk: goal?.id },
                          });
                    }
                    e.stopPropagation();
                  }}
                  className={`${
                    user.designation === "EMPLOYEE" ? "cursor-pointer" : null
                  }`}
                >
                  <TableCell align="center">{goal?.title || "N/A"}</TableCell>
                  <TableCell align="center">{goal?.email}</TableCell>

                  <TableCell align="center">{goal?.progress || "0"}%</TableCell>

                  <TableCell align="center">
                    {goal?.managerId === id ? "Own Created" : "Manager Created"}
                  </TableCell>

                  <TableCell align="center">
                    <div className="flex items-center justify-center gap-x-2">
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/goalsOrOkHours/edit/${goal?.id}`);
                        }}
                      >
                        Edit
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  No goals found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
};

export default LeaveDetail;
